// =====================================================================
// Reconciliation.jsx — M-008 — à scanner (Effitrace) vs scannés (convoyeur)
// =====================================================================
// Reproduit le panneau validé (zeus-reconcile.js) :
//   - 4 métriques (Total / Scannés / Restants / %)
//   - tableau par transporteur (pills cliquables -> drill-down)
//   - drill-down : Préparation, Partenaire, Client, Commande, N° suivi,
//     Poids, Packing (par/heure), Statut (scanné à / non scanné)
//   - export CSV
//   - bouton "Lancer le robot Effitrace" (gestion uniquement)
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import { convoyeur as convoyeurApi } from '../../services/api.js';
import { celluleCsv } from '../../lib/csv.js';
import {
  thStyle, tdStyle, btnPrimary, btnSuccess, btnGhost, CarrierChip, fmtWeight, isOfflineError,
} from './styles.jsx';

export default function Reconciliation({ canManage, onRobotDone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // drill-down
  const [detail, setDetail] = useState(null); // { carrier, status, rows, count }
  const [detailLoading, setDetailLoading] = useState(false);

  // robot
  const [robot, setRobot] = useState(null);
  const [robotBusy, setRobotBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setData(await convoyeurApi.reconcile());
    } catch (err) {
      setError(isOfflineError(err) ? 'Convoyeur hors-ligne — réconciliation indisponible.' : 'Erreur de chargement.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRobot = useCallback(() => {
    convoyeurApi.robotStatus().then(setRobot).catch(() => setRobot(null));
  }, []);

  useEffect(() => { load(); loadRobot(); }, [load, loadRobot]);

  async function openDetail(carrier, status) {
    setDetail({ carrier, status, rows: [], count: 0 });
    setDetailLoading(true);
    try {
      const d = await convoyeurApi.reconcileDetail(carrier, status);
      setDetail({ carrier, status, rows: d.rows || [], count: d.count || 0 });
    } catch {
      setDetail({ carrier, status, rows: [], count: 0, error: true });
    } finally {
      setDetailLoading(false);
    }
  }

  async function runRobot() {
    if (robotBusy) return;
    if (!window.confirm("Lancer le robot Effitrace ? Il récupère l'export du jour et intègre les nouveaux colis.")) return;
    setRobotBusy(true);
    try {
      await convoyeurApi.robotRun();
      // poll status quelques fois
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        try {
          const s = await convoyeurApi.robotStatus();
          setRobot(s);
          if (!s.running || tries > 40) {
            clearInterval(poll);
            setRobotBusy(false);
            if (!s.running) { load(); onRobotDone && onRobotDone(); }
          }
        } catch { clearInterval(poll); setRobotBusy(false); }
      }, 3000);
    } catch (err) {
      setRobotBusy(false);
      alert(isOfflineError(err) ? 'Convoyeur hors-ligne.' : 'Erreur au lancement du robot.');
    }
  }

  return (
    <div>
      {/* Barre robot (gestion) */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <button style={btnGhost} onClick={() => { load(); loadRobot(); }} disabled={loading}>↻ Actualiser</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {robot && (
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {robot.running ? '🤖 robot en cours…'
                : robot.lastFinishAt ? `Dernier import : ${robot.lastAdded != null ? `+${robot.lastAdded} colis` : 'ok'}`
                : robot.parcels != null ? `${robot.parcels} colis chargés` : ''}
            </span>
          )}
          {canManage && (
            <button style={{ ...btnPrimary, opacity: robotBusy ? 0.6 : 1, cursor: robotBusy ? 'wait' : 'pointer' }} onClick={runRobot} disabled={robotBusy}>
              {robotBusy ? '🤖 …' : '🤖 Lancer le robot Effitrace'}
            </button>
          )}
        </div>
      </div>

      {error && <div style={{ background: '#fef3c7', color: '#92400e', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement…</div>}

      {!loading && data && (
        <>
          {/* Métriques */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: '1.25rem' }}>
            <Metric label="À scanner (total)" value={data.total} color="#1e293b" />
            <Metric label="Scannés" value={data.scanned} color="#059669" />
            <Metric label="Restants" value={data.remaining} color="#b91c1c" />
            <Metric label="% scanné" value={`${data.pct}%`} color="#2563eb" />
          </div>

          {/* Tableau par transporteur */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead style={{ background: '#f9fafb' }}>
                <tr>
                  <th style={thStyle}>Transporteur</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>À scanner</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Scannés</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Restants</th>
                  <th style={{ ...thStyle, width: '34%' }}>Progression</th>
                </tr>
              </thead>
              <tbody>
                {data.lines.length === 0 && (
                  <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>Aucune donnée.</td></tr>
                )}
                {data.lines.map(l => (
                  <tr key={l.carrier} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={tdStyle}><CarrierChip carrier={l.carrier} small /></td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{l.total}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <button onClick={() => openDetail(l.carrier, 'scanned')} style={pill('#dcfce7', '#15803d')} title="Voir les colis scannés">{l.scanned}</button>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <button onClick={() => openDetail(l.carrier, 'rem')} style={pill('#fee2e2', '#b91c1c')} title="Voir les colis restants (non scannés)">{l.remaining}</button>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ width: `${l.pct}%`, height: '100%', background: l.pct >= 95 ? '#059669' : l.pct >= 70 ? '#f59e0b' : '#ef4444' }} />
                        </div>
                        <span style={{ fontSize: '0.75rem', color: '#6b7280', minWidth: 34, textAlign: 'right' }}>{l.pct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {detail && (
        <DetailModal
          detail={detail}
          loading={detailLoading}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.875rem 1rem' }}>
      <div style={{ fontSize: '1.75rem', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 4 }}>{label}</div>
    </div>
  );
}

const pill = (bg, fg) => ({
  background: bg, color: fg, border: 0, borderRadius: 999,
  padding: '2px 10px', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer',
});

function DetailModal({ detail, loading, onClose }) {
  const { carrier, status, rows } = detail;
  const isScanned = status === 'scanned';

  function exportCsv() {
    const headers = ['Préparation date', 'Préparation heure', 'Partenaire', 'Client', 'Commande', 'N° suivi', 'Poids (g)', 'Packing par', 'Packing heure', 'Statut', 'Scanné à'];
    const lines = rows.map(r => [
      r.prepDate || '', r.prepHeure || '', r.partner || '', r.client || '', r.commande || '', r.noSuivi || '',
      r.weight_g != null ? r.weight_g : '', r.controlePar || '', r.heureControle || '',
      r.scannedAt ? 'Scanné' : 'Non scanné', r.scannedAt || '',
    ].map(celluleCsv).join(';'));   // formule (= + - @) neutralisée : lib/csv.js
    const csv = '﻿' + [headers.join(';'), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reconciliation_${carrier}_${status}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{
      position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,.45)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 2vw',
    }}>
      <div style={{ background: '#fff', borderRadius: 12, width: 980, maxWidth: '96vw', maxHeight: '88vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(15,23,42,.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: '#fff' }}>
          <CarrierChip carrier={carrier} small />
          <strong style={{ color: '#1e293b' }}>{isScanned ? 'Colis scannés' : 'Colis restants (non scannés)'}</strong>
          <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>· {rows.length} colis</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button style={btnSuccess} onClick={exportCsv} disabled={!rows.length}>⬇ Export CSV</button>
            <button style={btnGhost} onClick={onClose}>Fermer</button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement…</div>
        ) : (
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead style={{ background: '#f9fafb' }}>
                <tr>
                  <th style={thStyle}>Préparation</th>
                  <th style={thStyle}>Partenaire</th>
                  <th style={thStyle}>Client</th>
                  <th style={thStyle}>Commande</th>
                  <th style={thStyle}>N° suivi</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Poids</th>
                  <th style={thStyle}>Packing (Effitrace)</th>
                  <th style={thStyle}>Statut</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>Aucun colis.</td></tr>
                )}
                {rows.map((r, i) => (
                  <tr key={(r.noSuivi || '') + i} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={tdStyle}>{r.prepDate || '—'}{r.prepHeure ? <span style={{ color: '#94a3b8' }}> · {r.prepHeure}</span> : ''}</td>
                    <td style={tdStyle}>{r.partner || '—'}</td>
                    <td style={tdStyle}>{r.client || '—'}</td>
                    <td style={tdStyle}>{r.commande || '—'}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.noSuivi || '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtWeight(r.weight_g)}</td>
                    <td style={tdStyle}>
                      {r.controlePar ? <>{r.controlePar}{r.heureControle ? <span style={{ color: '#94a3b8' }}> · {r.heureControle}</span> : ''}</> : <span style={{ color: '#b45309' }}>à contrôler</span>}
                    </td>
                    <td style={tdStyle}>
                      {r.scannedAt
                        ? <span style={{ color: '#15803d', background: '#dcfce7', fontSize: '0.6875rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6 }}>Scanné · {r.scannedAt}</span>
                        : <span style={{ color: '#b91c1c', background: '#fee2e2', fontSize: '0.6875rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6 }}>Non scanné</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
